import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Plus, Clock, Zap, Skull } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyTorches, type MyTorch } from '@/hooks/useMyTorches';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LoginArea } from '@/components/auth/LoginArea';

// ─── Live countdown ────────────────────────────────────────────────────────

function LiveCountdown({ targetTs }: { targetTs: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, targetTs - now);
  if (remaining === 0) return <span className="text-yellow-600 font-semibold text-xs">Dormant</span>;

  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  if (d > 0) return <span className="text-orange-600 font-mono font-semibold text-xs">{d}d {h}h {m}m</span>;
  if (h > 0) return <span className="text-orange-500 font-mono font-semibold text-xs">{h}h {m}m {s}s</span>;
  return <span className="text-red-500 font-mono font-bold text-xs animate-pulse">{m}m {s}s</span>;
}

// ─── Current holder mini ───────────────────────────────────────────────────

function CurrentHolderMini({ pubkey }: { pubkey: string }) {
  const { data: author } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-2 mt-2">
      <Avatar className="w-5 h-5 ring-1 ring-orange-400 ring-offset-1">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-[9px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <span className="text-xs text-muted-foreground truncate">
        <span className="text-muted-foreground/60">Held by </span>
        <span className="font-medium text-foreground">{displayName}</span>
      </span>
    </div>
  );
}

// ─── Torch card ────────────────────────────────────────────────────────────

function MyTorchCard({ torch }: { torch: MyTorch }) {
  const { genesis, parsed } = torch;

  const chain = useMemo(() =>
    parsed.canonicalChain.map(link => ({
      holderPubkey: link.pass.recipientPubkey,
      zapAmountSats: link.pass.zapAmountSats,
      positionIndex: link.position,
    })),
    [parsed]
  );

  const totalHolders = parsed.chainLength + 1;

  const stateBadge = parsed.state === 'lit'
    ? { label: '🔥 Lit', className: 'bg-orange-500' }
    : { label: '🪦 Gone', className: 'bg-gray-500' };

  return (
    <Link to={`/torch/${genesis.slug}`}>
      <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer h-full group">
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3 gap-2">
            <h3 className="text-base font-semibold group-hover:text-orange-600 transition-colors leading-tight line-clamp-2">
              {genesis.title}
            </h3>
            <Badge className={`${stateBadge.className} text-white text-xs shrink-0`}>
              {stateBadge.label}
            </Badge>
          </div>

          {/* Torch SVG */}
          <div className="flex justify-center mb-3">
            <TorchSvg
              genesisId={genesis.id}
              chain={chain}
              state={parsed.state}
              className="w-28 h-36 group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          {/* Current holder */}
          <CurrentHolderMini pubkey={parsed.currentHolder ?? genesis.pubkey} />

          {/* Stats row */}
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
            <span>{totalHolders} holder{totalHolders !== 1 ? 's' : ''}</span>
            {parsed.state === 'lit' ? (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <LiveCountdown targetTs={parsed.expiresAt} />
              </div>
            ) : (
              <span className="text-gray-400">Sealed</span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Skeleton card ─────────────────────────────────────────────────────────

function TorchCardSkeleton() {
  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-14" />
        </div>
        <div className="flex justify-center mb-3">
          <Skeleton className="w-28 h-36" />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-3 w-28" />
        </div>
        <div className="flex items-center justify-between mt-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────

function EmptyState({ icon, message, cta }: { icon: React.ReactNode; message: string; cta?: React.ReactNode }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-16 px-8 text-center">
        <div className="flex justify-center mb-4 text-muted-foreground">{icon}</div>
        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">{message}</p>
        {cta}
      </CardContent>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function MyTorches() {
  const { user } = useCurrentUser();
  const { data, isLoading } = useMyTorches(user?.pubkey);

  const litTorches = data?.lit ?? [];
  const holdingTorches = data?.holding ?? [];

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/15 border border-orange-500/20 mb-6">
            <Flame className="w-8 h-8 text-orange-400" />
          </div>
          <h1 className="text-2xl font-bold mb-3">My Torches</h1>
          <p className="text-muted-foreground mb-8">
            Sign in to see the torches you've lit and the ones currently in your hands.
          </p>
          <LoginArea className="max-w-48 mx-auto" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-400" />
              My Torches
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Torches you've lit and ones you're currently holding.
            </p>
          </div>
          <Link to="/create">
            <Button className="bg-orange-500 hover:bg-orange-600 gap-2">
              <Plus className="w-4 h-4" />
              Light a Torch
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="holding">
          <TabsList className="mb-8 h-auto bg-transparent p-0 gap-3 flex">
            <TabsTrigger
              value="holding"
              className="flex-1 flex-col items-start gap-1 rounded-xl border border-border/60 bg-card/60 px-5 py-4 text-left shadow-sm
                data-[state=active]:border-orange-500/60 data-[state=active]:bg-orange-500/10 data-[state=active]:shadow-[0_0_16px_rgba(249,115,22,0.12)]
                hover:border-orange-500/40 transition-all h-auto"
            >
              <div className="flex items-center gap-2 w-full">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-orange-500/15">
                  <Zap className="w-3.5 h-3.5 text-orange-400" />
                </div>
                <span className="font-semibold text-sm">Holding</span>
                {!isLoading && (
                  <span className="ml-auto text-lg font-bold text-orange-400">{holdingTorches.length}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-normal pl-9">
                Live torches currently passed to you
              </p>
            </TabsTrigger>

            <TabsTrigger
              value="lit"
              className="flex-1 flex-col items-start gap-1 rounded-xl border border-border/60 bg-card/60 px-5 py-4 text-left shadow-sm
                data-[state=active]:border-amber-500/60 data-[state=active]:bg-amber-500/10 data-[state=active]:shadow-[0_0_16px_rgba(245,158,11,0.12)]
                hover:border-amber-500/40 transition-all h-auto"
            >
              <div className="flex items-center gap-2 w-full">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/15">
                  <Flame className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <span className="font-semibold text-sm">I Lit</span>
                {!isLoading && (
                  <span className="ml-auto text-lg font-bold text-amber-400">{litTorches.length}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-normal pl-9">
                Torches you created and sent into the world
              </p>
            </TabsTrigger>
          </TabsList>

          {/* ── Holding tab ── */}
          <TabsContent value="holding">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <TorchCardSkeleton key={i} />)}
              </div>
            ) : holdingTorches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {holdingTorches.map(t => (
                  <MyTorchCard key={t.genesis.id} torch={t} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<Zap className="w-10 h-10" />}
                message="You're not holding any live torches right now. When someone passes you one, it'll appear here."
              />
            )}
          </TabsContent>

          {/* ── I Lit tab ── */}
          <TabsContent value="lit">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <TorchCardSkeleton key={i} />)}
              </div>
            ) : litTorches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {litTorches.map(t => (
                  <MyTorchCard key={t.genesis.id} torch={t} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<Skull className="w-10 h-10" />}
                message="You haven't lit any torches yet. Be the first to start a chain."
                cta={
                  <Link to="/create">
                    <Button className="bg-orange-500 hover:bg-orange-600 gap-2">
                      <Plus className="w-4 h-4" />
                      Light a Torch
                    </Button>
                  </Link>
                }
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
